import { type DataTableColumn, IconButton } from "@carbon6/c6-frontend-design";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { useSearchParams } from "react-router";
import type { TrainingSessionListView } from "@/api";
import { useAppState } from "@/app";
import { HatDataTable } from "@/components/table/HatDataTable";
import { HatTextTooltip } from "@/components/tooltip";
import { HatTextModal } from "@/components/modal";
import { PaginationFooter } from "@/components/browse";
import { FilterResultCount } from "@/components/filter";
import { BulkActionBar } from "@/components/table/BulkActionBar";
import bulkBarStyles from "@/components/table/BulkActionBar.module.css";
import selectionStyles from "@/components/table/dataTableSelection.module.css";
import { SessionComparePanel } from "./compare/SessionComparePanel";
import { assignSessionCompareSlots } from "./compare/sessionCompareModel";
import {
  applySessionDelete,
  deleteConfirmText,
  resolveSessionDeleteErrorMessage,
  sessionDeleteActionText,
} from "./sessionDeleteActions";
import { SessionFilterBar } from "./SessionFilterBar";
import { readSessionFilters, writeSessionFilters, type SessionFilterState } from "./sessionFilters";
import { clearLegacyWorkspaceParam, hasLegacyWorkspaceParam } from "./policyFilterParams";
import { openSessionDatasetsWindow } from "./sessionDatasets/sessionDatasetsWindow";
import { SessionNameCell } from "./SessionNameCell";
import { SessionNoteCell } from "./SessionNoteCell";
import { SubmitSessionRowActions } from "./SubmitSessionRowActions";
import { SubmitSessionTableRow } from "./SubmitSessionTableRow";
import { SubmitSessionTableSource } from "./SubmitSessionTableSource";
import { sessionDetailedStatus } from "@/modules/training/public";
import { SessionStatusChip } from "@/components/status/SessionStatusChip";
import { useSessionFilterOptions } from "./useSessionFilterOptions";
import { DatasetRefCell } from "./DatasetRefCell";
import { openTrainingSessionWindow } from "./trainingSessionWindow";
import { subscribeTrainingSubmitted } from "./trainingSubmitBus";
import styles from "./SubmitSessionsPanel.module.css";

const PAGE_SIZE = 50;

// 호출부가 scope 를 명시한다. me 는 내 세션, team 은 고른 팀의 groupUuid 스코프다. aria/empty/
// min-width 같은 표시 정책은 panel 내부에서 scope 별로 정한다. groupFilter 는 페이지가 소유한
// 그룹 컨트롤을 필터 줄에 넣는 슬롯이다. 상태는 페이지가 가지고, 패널은 표시 위치만 정한다.
// My/Team 스위처와 새 학습 진입점은 페이지 헤더(제목 줄)가 소유한다.
type SubmitSessionsPanelProps =
  | { scope: "me"; groupUuid?: never; groupFilter?: never }
  | { scope: "team"; groupUuid: string | null; groupFilter?: ReactNode };

// 세션 목록 패널. 두 스코프가 함께 쓴다(같은 서버 row). 행 클릭은 그 row 로 상세 Window 를
// 연다. 가시성은 workspace 선점 스코프라 어느 행이든 rich 응답을 받는다. 소유 판별은 상세
// 컨트롤러가 한다. 컨트롤러는 행의 registry_user(선점자)와 내 member uuid 를 비교한다.
// status 서버 필터와 prev/next 페이징을 쓴다.
export function SubmitSessionsPanel(props: SubmitSessionsPanelProps) {
  const { scope } = props;
  const groupUuid = props.scope === "team" ? props.groupUuid : null;
  // 조회 축은 URL 이 소유한다. 그래서 새로고침·딥링크·뒤로가기 뒤에도 남는다. 필터 조정은
  // 히스토리 엔트리를 교체한다(usePageParam 컨벤션). 그래서 Back 은 필터가 아니라 페이지 단위로 이동한다.
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => readSessionFilters(searchParams), [searchParams]);
  // Me 에는 member 축이 없다. URL 정리 effect 가 실행되기 전 렌더에서도 오래된 member 를 source
  // 서명이나 칩에 넣지 않는다. 그래서 같은 와이어 요청을 다시 만들거나 잠깐 노출하지 않는다.
  const effectiveFilters = useMemo(
    () =>
      scope === "me" && filters.memberUuid !== null ? { ...filters, memberUuid: null } : filters,
    [scope, filters],
  );
  const handleFiltersChange = useCallback(
    (next: SessionFilterState) => {
      setSearchParams((prev) => writeSessionFilters(prev, next), { replace: true });
    },
    [setSearchParams],
  );
  const filterOptions = useSessionFilterOptions(scope, groupUuid, effectiveFilters.memberUuid);

  // URL 은 외부 입력이다. 후보 목록이 확정되면 현재 스코프에 없는 멤버·실험을 정규화한다.
  // 멤버가 무효면 그 아래 실험도 지운다. 이전 ?workspace= 키 정리도 같은 replace 커밋에 합친다.
  // 그래서 여러 effect 가 같은 URL 스냅샷을 서로 덮어쓰는 경쟁이 생기지 않는다.
  useEffect(() => {
    let nextFilters = filters;
    if (scope === "me" && filters.memberUuid !== null) {
      nextFilters = { ...nextFilters, memberUuid: null };
    } else if (
      scope === "team" &&
      groupUuid !== null &&
      filters.memberUuid !== null &&
      filterOptions.membersStatus === "success" &&
      filterOptions.memberNameById[filters.memberUuid] === undefined
    ) {
      nextFilters = { ...nextFilters, memberUuid: null, experimentId: null };
    }
    if (
      nextFilters.experimentId !== null &&
      !(scope === "team" && groupUuid === null) &&
      filterOptions.experimentsStatus === "success" &&
      filterOptions.experimentNameById[nextFilters.experimentId] === undefined
    ) {
      nextFilters = { ...nextFilters, experimentId: null };
    }
    if (nextFilters === filters && !hasLegacyWorkspaceParam(searchParams)) {
      return;
    }
    setSearchParams(
      (prev) =>
        clearLegacyWorkspaceParam(
          nextFilters === filters ? prev : writeSessionFilters(prev, nextFilters),
        ),
      { replace: true },
    );
  }, [scope, groupUuid, filters, filterOptions, searchParams, setSearchParams]);
  // 일괄 삭제 확인. 되돌릴 수 있는 기간이 유한한 액션이라 선택 개수를 다시 표시하고 묻는다.
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // 비교 팝업은 이 패널의 로컬 상태가 소유한다. 창 스택 밖이라 최소화 없이 열기/닫기만 한다.
  // 목록 행 스냅샷과 여는 시점에 고정한 색 슬롯을 함께 보관한다. 그래서 로드 순서로 색이 바뀌지 않는다.
  const [compare, setCompare] = useState<{
    sessions: TrainingSessionListView[];
    slotById: Record<string, number>;
  } | null>(null);

  const [source] = useState(() => {
    const created = new SubmitSessionTableSource({
      scope: props.scope === "team" ? { kind: "team", groupUuid: props.groupUuid } : { kind: "me" },
      pageSize: PAGE_SIZE,
      rowClickHandler: (view) => openTrainingSessionWindow(view),
      // 행 ⋯ 메뉴는 자기 상태(프로브·이름 모달)를 가지므로 컴포넌트가 소유한다. source 는 어느
      // 행에 무엇을 표시할지만 전달한다. 이름이나 삭제 예정 상태가 바뀌면 행의 값이 오래된
      // 값이 된다. 그래서 source 를 가진 이쪽이 현재 페이지를 다시 받는다.
      rowActionsBuilder: (row) => (
        <SubmitSessionRowActions
          view={row.getView()}
          onSessionChanged={() => void created.refresh()}
        />
      ),
      resolveErrorMessage: resolveSessionsError,
      initialFilters: effectiveFilters,
    });
    return created;
  });

  useEffect(() => {
    void source.initialize();
    return () => source.dispose();
  }, [source]);

  // 제출 위저드는 이 목록 위에 뜨는 창이다. 제출이 성공해도 아래 목록은 그대로다. 그래서 방송을
  // 받아 현재 페이지를 다시 받는다. 서버는 제출 응답 시점에 새 세션을 이미 커밋한다. 목록 정렬은
  // created_at desc 다. 상태 필터가 새 세션의 대기 상태를 제외하지 않으면 새 세션은 1페이지 맨 위에 온다.
  useEffect(() => subscribeTrainingSubmitted(() => void source.refresh()), [source]);

  // 그룹과 URL 필터를 한 번에 적용한다. 그래서 한 커밋은 1페이지 요청 하나만 만든다.
  useEffect(() => {
    void source.setQuery(groupUuid, effectiveFilters);
  }, [source, groupUuid, effectiveFilters]);

  // 노트 저장·삭제는 행의 미리보기를 오래된 값으로 만든다. source 를 가진 여기서 현재 페이지를 다시 받는다.
  const refreshAfterNoteChange = useCallback(() => void source.refresh(), [source]);
  const columns = useMemo(
    () => buildSubmitSessionColumns(scope, refreshAfterNoteChange),
    [scope, refreshAfterNoteChange],
  );
  const snapshot = useSyncExternalStore(source.subscribe, source.getSnapshot);
  const selectedCount = snapshot.selectedIds.size;
  const isLoading = snapshot.loadState === "loading";

  const { state: appState } = useAppState();
  const myMemberUuid = appState.currentUser?.memberUuid ?? null;
  const isAdmin = appState.currentUser?.role === "admin";
  const selectedRows = selectedCount > 0 ? source.getSelectedRows() : [];
  // 삭제는 실행 중인 학습을 취소한다. 그래서 행 메뉴와 같은 선점자/admin 게이트로 좁힌다. 하나라도
  // 다른 사람의 workspace 면 일괄 삭제도 열지 않는다(서버는 스코프만 확인한다).
  const deleteBlockedReason = resolveBulkDeleteBlock({
    selectedRows,
    myMemberUuid,
    isAdmin,
  });
  const closeDeleteConfirm = () => {
    if (!deleting) {
      setConfirmingDelete(false);
      setDeleteError(null);
    }
  };
  const submitBulkDelete = () => {
    if (deleting) {
      return;
    }
    setDeleting(true);
    setDeleteError(null);
    void applySessionDelete("delete", {
      sessionIds: selectedRows.map((row) => row.id),
    })
      .then(() => {
        setConfirmingDelete(false);
        // [삭제 예정 포함] 이 꺼져 있으면 삭제 예정 행은 목록에서 빠진다. 그래서 선택도 함께 버린다.
        source.clearSelection();
        void source.refresh();
      })
      .catch((error: unknown) => {
        setDeleteError(resolveSessionDeleteErrorMessage("delete", error));
      })
      .finally(() => setDeleting(false));
  };
  const deleteConfirm = deleteConfirmText(selectedCount);

  return (
    <section
      className={styles.panel}
      aria-label={scope === "team" ? "팀 Training Session" : "내 Training Session"}
    >
      {/* 조회 축을 먼저 둔다. 무엇을 볼지 정한 다음에 고른 행으로 할 일을 정한다.
          팀 그룹은 스코프에 딸린 축이라 검색 바로 뒤에 둔다. 도구 스트립 host 는 [바 | 표] 두
          행 그리드다. 그 안에 넣으면 바가 늘어난다. 그래서 밖에 둔다. */}
      <SessionFilterBar
        filters={effectiveFilters}
        onChange={handleFiltersChange}
        options={filterOptions}
        showMemberFilter={scope === "team"}
        leadingControls={props.scope === "team" ? props.groupFilter : null}
      />
      {/* selectionColumn: 선택 열 대비 보정(라이트에서 가능/불가 구분). host: 표 상단
          스트립(비교 액션·개수·해제)을 표와 한 요소로 묶는다. 자식은 바와 표 두 개뿐이다. */}
      <div
        className={`${styles.tableArea} ${selectionStyles.selectionColumn} ${bulkBarStyles.host}`}
      >
        <BulkActionBar
          source={source}
          ariaLabel={
            scope === "team" ? "팀 Training Session 목록 도구" : "내 Training Session 목록 도구"
          }
          // 결과 수는 조건이 아니라 그 결과다. 그래서 조건 줄이 아니라 표 바로 위 도구 띠 왼쪽에
          // 둔다. 선택이 생기면 개수 문구가 그 자리를 차지한다(BulkActionBar 계약). 개수 문구가
          // 활성 상태를 알리는 자리라, 대기 상태의 결과 수는 muted 톤을 쓴다.
          idleContent={
            snapshot.loadState === "loaded" ? (
              <FilterResultCount count={snapshot.total} unitLabel="Session" />
            ) : null
          }
          actions={
            <>
              {/* 비교는 두 세션부터 뜻이 있다. 자리를 유지해 표에서 선택으로 무엇을 할 수 있는지 알린다. */}
              <IconButton
                text="비교"
                type="button"
                size="sm"
                variant="solid"
                disabled={selectedCount < 2}
                title={
                  selectedCount < 2 ? "비교하려면 Session을 두 개 이상 선택하세요." : undefined
                }
                onClick={() => {
                  const sessions = source.getSelectedRows().map((row) => row.getView());
                  setCompare({
                    sessions,
                    slotById: assignSessionCompareSlots(sessions.map((session) => session.id)),
                  });
                }}
              />
              <IconButton
                text={sessionDeleteActionText.delete.label}
                type="button"
                size="sm"
                variant="solid"
                disabled={deleteBlockedReason !== null}
                title={deleteBlockedReason ?? undefined}
                onClick={() => {
                  setDeleteError(null);
                  setConfirmingDelete(true);
                }}
              />
            </>
          }
          // 새로고침(띠 우측, sm, rules.md §17). 세션 목록은 폴링하지 않는다. 그래서 진행 중인
          // 학습의 상태·진행이 바뀌어도 자동으로 오지 않는다. 이 버튼만 다시 받을 수 있다.
          rightContent={
            <IconButton
              size="sm"
              text={isLoading ? "로딩 중…" : "새로고침"}
              variant="surface"
              disabled={isLoading}
              onClick={() => void source.refresh()}
            />
          }
        />
        <HatDataTable
          source={source}
          columns={columns}
          emptyMessage={
            scope === "team"
              ? "팀에 표시할 Training Session이 없습니다."
              : "제출한 Training Session이 없습니다. [새 Training] 으로 시작해 보세요."
          }
          loadingMessage="Session을 불러오는 중입니다."
          ariaLabel={scope === "team" ? "팀 Training Session 목록" : "내 Training Session 목록"}
          scrollable
          // 공유/다운로드 버튼과 더 보기 메뉴가 함께 들어갈 폭을 확보한다.
          actionsColumnWidth="168px"
          minWidth={scope === "team" ? "984px" : "864px"}
          showCheckbox
          showIndex={false}
        />
      </div>
      <PaginationFooter
        page={snapshot.page}
        size={snapshot.size}
        total={snapshot.total}
        isLoading={snapshot.loadState === "loading"}
        ariaLabel={scope === "team" ? "팀 제출 Session 페이지 이동" : "제출 Session 페이지 이동"}
        mode="compact"
        hideWhenSinglePage
        onPrevious={() => void source.goToPage(Math.max(1, snapshot.page - 1))}
        onNext={() => void source.goToPage(snapshot.page + 1)}
      />
      {compare !== null ? (
        <SessionComparePanel
          sessions={compare.sessions}
          slotById={compare.slotById}
          onClose={() => setCompare(null)}
        />
      ) : null}
      <HatTextModal
        open={confirmingDelete}
        label={deleteConfirm.title}
        caption={deleteConfirm.caption}
        error
        okText={sessionDeleteActionText.delete.label}
        busy={deleting}
        errorMessage={deleteError ?? undefined}
        onOkPress={submitBulkDelete}
        onCancelPress={closeDeleteConfirm}
      />
    </section>
  );
}

// 컬럼 순서: 생성일 · (Team 멤버) · 실험/학습 세션 · Dataset · 상태(단계·진행률) · 노트. Me 는
// actor 의 workspace 합집합이다. 그러나 workspace 는 연구자 화면의 필터·표시 어휘로 노출하지 않는다.
function buildSubmitSessionColumns(
  scope: "me" | "team",
  onNoteChanged: () => void,
): readonly DataTableColumn<SubmitSessionTableRow>[] {
  return [
    { key: "createdLabel", header: "생성일", width: "150px" },
    ...(scope === "team"
      ? [
          {
            key: "registryUserLabel",
            header: "멤버 이름",
            width: "minmax(120px, 0.9fr)",
          } satisfies DataTableColumn<SubmitSessionTableRow>,
        ]
      : []),
    {
      // 삭제 예정 칩은 이름 앞에 두고 상태 칩을 덮지 않는다. 삭제 예정은 상태 축과 독립이므로
      // 그 세션도 성공·실패를 그대로 표시해야 한다. 이 행은 [삭제 예정 포함] 을 켰을 때만 온다.
      // 칸에 머물면 목록 행 값으로 채운 상세 카드가 뜬다(SessionNameCell).
      key: "displayName",
      header: "실험 / Training Session",
      width: "minmax(240px, 1.6fr)",
      render: (row) => <SessionNameCell row={row} />,
    },
    {
      key: "datasetLabel",
      header: "Dataset",
      width: "minmax(360px, 3fr)",
      cellPadding: "tight",
      render: (row) => (
        <DatasetRefCell
          registryRef={row.datasetRef}
          fallbackLabel={row.datasetLabel}
          extraCount={row.datasetExtraCount}
          onExtraClick={() => openSessionDatasetsWindow(row.getView())}
        />
      ),
    },
    {
      // 실행 중에는 단계와 실제 진행률을 한 칩에 합친다. 대기·실패 등의 세부 사유는 툴팁에 둔다.
      key: "status",
      header: "상태",
      width: "120px",
      render: (row) => {
        const detailed = sessionDetailedStatus(row.status);
        const chip = (
          <SessionStatusChip status={row.status} showIcon={false}>
            {row.statusLabel}
          </SessionStatusChip>
        );
        return detailed.label === row.statusLabel ? (
          chip
        ) : (
          <HatTextTooltip text={detailed.label} placement="top">
            {chip}
          </HatTextTooltip>
        );
      },
    },
    {
      // 트리거 하나가 유무 표시·hover 미리보기·클릭 편집을 모두 한다(SessionNoteCell). 미리보기와
      // 팝업은 모두 표 오른쪽 끝에 있으므로 **왼쪽으로** 펼친다.
      key: "noteLabel",
      header: "노트",
      width: "44px",
      align: "center",
      render: (row) => <SessionNoteCell view={row.getView()} onNoteChanged={onNoteChanged} />,
    },
  ];
}

// 일괄 삭제를 막는 사유(없으면 null). 사유를 하나씩 구분하는 이유는 버튼 title 이 그대로 안내가
// 되기 때문이다. 왜 눌리지 않는지 알리지 않으면 권한 문제인지 선택 문제인지 알 수 없다.
function resolveBulkDeleteBlock({
  selectedRows,
  myMemberUuid,
  isAdmin,
}: {
  selectedRows: readonly SubmitSessionTableRow[];
  myMemberUuid: string | null;
  isAdmin: boolean;
}): string | null {
  if (selectedRows.length === 0) {
    return "삭제할 Session을 선택하세요.";
  }
  if (selectedRows.some((row) => row.deleted)) {
    return "이미 삭제 예정인 Session이 선택에 섞여 있습니다.";
  }
  const allMine = selectedRows.every(
    (row) =>
      isAdmin || (myMemberUuid !== null && row.getView().registry_user?.uuid === myMemberUuid),
  );
  return allMine
    ? null
    : "내가 선점한 Session만 삭제할 수 있습니다(남의 Session이 선택에 섞여 있습니다).";
}

function resolveSessionsError(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return "Session을 불러오지 못했습니다.";
}
